import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Users, Sparkles, CircleDot, UserCog } from 'lucide-react'
import { useProject } from './ProjectLayout'
import { TeamMembersPane } from './TeamMembers'
import { Avatar, Card, Chip, Tabs, cn } from '../../design'
import { getTeamWorkload, getSkillMatrix, type WorkloadRow, type WorkloadBubble } from '../../api/team'
import { lookupSprints } from '../../api/users'

/**
 * Phase 10 — Team Workload V1+V2+V4 (per `wf-timeline-team.jsx` Team section).
 *  V1 Roster — load bars per user
 *  V2 Skill matrix — user × skill grid, dot size = level
 *  V4 Bubble cluster — epic = bubble sized by effort, wedges per person
 */
export default function Team() {
  const project = useProject()
  const sprintsQ = useQuery({
    queryKey: ['lookups', 'sprints', project.id],
    queryFn: () => lookupSprints(project.id),
    staleTime: 60_000,
  })
  const sprints = sprintsQ.data ?? []
  const initial = sprints.find(s => s.status === 'active') ?? sprints[0]
  const [scope, setScope] = useState<'sprint' | 'project'>('sprint')
  const [sprintId, setSprintId] = useState<number | null>(initial?.id ?? null)
  if (!sprintId && initial) setSprintId(initial.id)

  return (
    <div className="flex flex-col h-full">
      <div className="px-6 py-4 border-b border-border bg-bg-elevated">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="text-base font-semibold tracking-tight">Team Workload</h2>
            <p className="text-xs text-fg-subtle">
              Roster, skills, and epic distribution. Switch scope to compare sprint vs project totals.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="inline-flex rounded-md border border-border overflow-hidden">
              <ToggleBtn active={scope === 'sprint'}  onClick={() => setScope('sprint')}>Sprint</ToggleBtn>
              <ToggleBtn active={scope === 'project'} onClick={() => setScope('project')}>Project</ToggleBtn>
            </div>
            {scope === 'sprint' && sprints.length > 0 && (
              <select
                value={sprintId ?? ''}
                onChange={e => setSprintId(Number(e.target.value))}
                className="rounded-md border border-border bg-bg-elevated px-3 py-1.5 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-accent/30"
              >
                {sprints.map(s => (
                  <option key={s.id} value={s.id}>{s.name}{s.status === 'active' ? ' · active' : ''}</option>
                ))}
              </select>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-auto">
        <div className="mx-auto max-w-7xl px-6 py-6">
          <Tabs
            items={[
              { value: 'roster',  label: <TabLabel icon={<Users size={13} />}>Roster</TabLabel>,
                content: <RosterPane projectId={project.id} sprintId={scope === 'sprint' ? sprintId : null} scope={scope} /> },
              { value: 'skills',  label: <TabLabel icon={<Sparkles size={13} />}>Skill matrix</TabLabel>,
                content: <SkillMatrixPane /> },
              { value: 'bubbles', label: <TabLabel icon={<CircleDot size={13} />}>Bubbles</TabLabel>,
                content: <BubblePane projectId={project.id} sprintId={scope === 'sprint' ? sprintId : null} scope={scope} /> },
              { value: 'members', label: <TabLabel icon={<UserCog size={13} />}>Members</TabLabel>,
                content: <TeamMembersPane /> },
            ]}
            defaultValue="roster"
          />
        </div>
      </div>
    </div>
  )
}

function TabLabel({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return <span className="inline-flex items-center gap-1.5">{icon}{children}</span>
}
function ToggleBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'px-3 py-1.5 text-xs font-medium',
        active ? 'bg-accent text-accent-fg' : 'bg-bg-elevated text-fg-muted hover:bg-bg-muted',
      )}
    >
      {children}
    </button>
  )
}

// ─── V1 Roster ───────────────────────────────────────────────────────────

function RosterPane({ projectId, sprintId, scope }: { projectId: number; sprintId: number | null; scope: 'sprint' | 'project' }) {
  const wQ = useQuery({
    queryKey: ['team-workload', scope, projectId, sprintId],
    queryFn: () => getTeamWorkload(scope === 'sprint' && sprintId ? { sprint_id: sprintId } : { project_id: projectId }),
    refetchInterval: 15_000,
  })
  const rows = wQ.data?.rows ?? []

  return (
    <div className="mt-4 space-y-3">
      <p className="text-xs text-fg-subtle italic">
        Red bar = over capacity. Click a row to filter the backlog by that assignee (coming soon).
      </p>
      <Card padded={false}>
        {wQ.isLoading && <div className="px-5 py-8 text-center text-sm text-fg-subtle">Loading…</div>}
        {!wQ.isLoading && rows.length === 0 && (
          <div className="px-5 py-8 text-center text-sm text-fg-subtle">No team members assigned to this scope.</div>
        )}
        <ul className="divide-y divide-border">
          {rows.map(r => <RosterRow key={r.user_id} row={r} />)}
        </ul>
      </Card>
    </div>
  )
}

function RosterRow({ row }: { row: WorkloadRow }) {
  const over = row.load_pct > 100
  const fill = Math.min(100, row.load_pct)
  const overflow = Math.min(100, Math.max(0, row.load_pct - 100))
  return (
    <li className="px-5 py-3 grid items-center gap-4"
        style={{ gridTemplateColumns: 'auto 160px 140px 1fr 90px' }}>
      <Avatar name={row.name} color={row.avatar_color ?? '#9aa0a6'} size="md" />
      <div className="min-w-0">
        <div className="text-sm font-semibold tracking-tight truncate">{row.name}</div>
        <div className="text-[10px] text-fg-subtle truncate">{row.email}</div>
      </div>
      {row.focus ? (
        <Chip
          tone="epic"
          color={row.focus.epic_color ?? '#9aa0a6'}
          dot
          size="sm"
          className="self-center justify-self-start"
        >
          {row.focus.epic_name}
        </Chip>
      ) : (
        <span className="text-[11px] text-fg-disabled italic">no focus</span>
      )}
      <div className="relative">
        <div className="relative h-3.5 rounded-full bg-bg-muted border border-border overflow-hidden">
          <div
            className={cn('h-full transition-all', over ? 'bg-danger' : 'bg-fg')}
            style={{ width: `${fill}%` }}
          />
          {over && (
            <div
              className="absolute top-0 h-full bg-danger/40"
              style={{
                left: '100%',
                width: `${overflow}%`,
                backgroundImage:
                  'repeating-linear-gradient(-45deg, transparent 0 4px, rgba(196,82,74,0.6) 4px 6px)',
              }}
            />
          )}
        </div>
        <span className={cn(
          'absolute -top-5 right-0 text-[10px] font-mono',
          over ? 'text-danger font-semibold' : 'text-fg-subtle',
        )}>
          {row.load_pct}%
        </span>
      </div>
      <div className="text-right">
        <div className="font-mono text-xs">{row.total_points}pt</div>
        <div className="text-[10px] text-fg-subtle">
          {row.story_count} {row.story_count === 1 ? 'story' : 'stories'}
        </div>
      </div>
    </li>
  )
}

// ─── V2 Skill Matrix ─────────────────────────────────────────────────────

const SKILL_COLORS = ['', 'var(--color-bg-muted)', 'var(--color-info)', 'var(--color-warning)', 'var(--color-accent)']

function SkillMatrixPane() {
  const mQ = useQuery({
    queryKey: ['skill-matrix'],
    queryFn: getSkillMatrix,
    staleTime: 60_000,
  })
  const matrix = mQ.data

  return (
    <div className="mt-4 space-y-3">
      <p className="text-xs text-fg-subtle italic">
        Match stories to skill stacks. Dot size = level (1 newbie · 4 expert).
      </p>
      <Card>
        {mQ.isLoading && <p className="text-sm text-fg-subtle text-center py-4">Loading…</p>}
        {matrix && matrix.skills.length === 0 && (
          <p className="text-sm text-fg-subtle text-center py-4">No skills configured yet.</p>
        )}
        {matrix && matrix.skills.length > 0 && (
          <>
            <div
              className="grid items-center"
              style={{ gridTemplateColumns: `160px repeat(${matrix.skills.length}, 1fr)` }}
            >
              <div />
              {matrix.skills.map(s => (
                <div key={s.id} className="text-center text-xs font-semibold uppercase tracking-wider text-fg-muted pb-2 border-b border-border">
                  {s.name}
                </div>
              ))}
              {matrix.rows.map(row => (
                <RowFragment key={row.user.id} row={row} />
              ))}
            </div>
            <SkillLegend />
          </>
        )}
      </Card>
    </div>
  )
}

function RowFragment({ row }: { row: import('../../api/team').SkillRow }) {
  return (
    <>
      <div className="flex items-center gap-2 py-3 border-b border-border last:border-b-0">
        <Avatar size="sm" name={row.user.name} color={row.user.avatar_color ?? '#9aa0a6'} />
        <span className="text-sm font-medium truncate">{row.user.name}</span>
      </div>
      {row.levels.map(l => (
        <div key={l.skill_id} className="flex items-center justify-center py-3 border-b border-border last:border-b-0">
          {l.level === 0 ? (
            <span className="text-fg-disabled">—</span>
          ) : (
            <div
              className="rounded-full border border-border-strong/30"
              style={{
                width:  12 + l.level * 6,
                height: 12 + l.level * 6,
                backgroundColor: SKILL_COLORS[l.level] || 'var(--color-accent)',
              }}
              title={`${l.name} · level ${l.level}`}
            />
          )}
        </div>
      ))}
    </>
  )
}

function SkillLegend() {
  return (
    <div className="mt-4 flex justify-center items-center gap-5 text-xs text-fg-subtle">
      {[1, 2, 3, 4].map(l => (
        <span key={l} className="inline-flex items-center gap-1.5">
          <span
            className="rounded-full border border-border-strong/30"
            style={{
              width:  6 + l * 3,
              height: 6 + l * 3,
              backgroundColor: SKILL_COLORS[l],
            }}
          />
          {['', 'newbie', 'ok', 'strong', 'expert'][l]}
        </span>
      ))}
    </div>
  )
}

// ─── V4 Bubble Cluster ───────────────────────────────────────────────────

function BubblePane({ projectId, sprintId, scope }: { projectId: number; sprintId: number | null; scope: 'sprint' | 'project' }) {
  const wQ = useQuery({
    queryKey: ['team-workload', scope, projectId, sprintId],
    queryFn: () => getTeamWorkload(scope === 'sprint' && sprintId ? { sprint_id: sprintId } : { project_id: projectId }),
  })
  const bubbles = wQ.data?.bubbles ?? []

  // Compute layout: pack into a grid based on relative size, simple grid placement
  const maxPts = Math.max(...bubbles.map(b => b.total_points), 1)
  const minRadius = 50
  const maxRadius = 130
  const radius = (b: WorkloadBubble) =>
    minRadius + (maxRadius - minRadius) * Math.sqrt((b.total_points || 0) / maxPts)

  // Place in a 2x2 grid (or wider) — 2 bubbles per row
  const positioned = useMemo(() => {
    const cols = Math.min(3, Math.ceil(Math.sqrt(bubbles.length)))
    const cellW = 280, cellH = 280
    return bubbles.map((b, i) => {
      const row = Math.floor(i / cols)
      const col = i % cols
      return {
        bubble: b,
        x: col * cellW + cellW / 2,
        y: row * cellH + cellH / 2,
        r: radius(b),
      }
    })
  }, [bubbles])

  const cols = Math.min(3, Math.ceil(Math.sqrt(bubbles.length || 1)))
  const rows = Math.ceil(bubbles.length / cols)
  const w = cols * 280
  const h = Math.max(1, rows) * 280

  if (wQ.isLoading) {
    return <p className="text-sm text-fg-subtle mt-6 text-center">Loading…</p>
  }
  if (bubbles.length === 0) {
    return <p className="text-sm text-fg-subtle mt-6 text-center">No epic effort to render.</p>
  }

  return (
    <div className="mt-4">
      <Card padded>
        <p className="text-xs text-fg-subtle italic mb-4">
          Bubble = epic, sized by total effort. Wedges show % of each person on it.
        </p>
        <div className="overflow-auto">
          <svg viewBox={`0 0 ${w} ${h}`} className="w-full" style={{ minHeight: h }}>
            {positioned.map(({ bubble, x, y, r }) => (
              <Bubble key={bubble.id} bubble={bubble} cx={x} cy={y} r={r} />
            ))}
          </svg>
        </div>
      </Card>
    </div>
  )
}

function Bubble({ bubble, cx, cy, r }: { bubble: WorkloadBubble; cx: number; cy: number; r: number }) {
  const total = bubble.people.reduce((a, p) => a + p.points, 0)
  // Pie wedges by person's points share
  let acc = 0
  const wedges = bubble.people.map(p => {
    const a0 = (acc / Math.max(1, total)) * 2 * Math.PI - Math.PI / 2
    acc += p.points
    const a1 = (acc / Math.max(1, total)) * 2 * Math.PI - Math.PI / 2
    const x0 = cx + Math.cos(a0) * r
    const y0 = cy + Math.sin(a0) * r
    const x1 = cx + Math.cos(a1) * r
    const y1 = cy + Math.sin(a1) * r
    const large = a1 - a0 > Math.PI ? 1 : 0
    return {
      p,
      d: `M ${cx} ${cy} L ${x0} ${y0} A ${r} ${r} 0 ${large} 1 ${x1} ${y1} Z`,
    }
  })

  const color = bubble.color ?? '#9aa0a6'

  return (
    <g>
      {/* Outer ring background */}
      <circle cx={cx} cy={cy} r={r} fill={`${color}20`} stroke={color} strokeWidth={2} />
      {/* Wedges */}
      {wedges.map((w, i) => (
        <path
          key={i}
          d={w.d}
          fill={w.p.avatar_color ?? color}
          opacity={0.55}
          stroke="white"
          strokeWidth={1}
        >
          <title>{w.p.name} · {w.p.points}pt</title>
        </path>
      ))}
      {/* Inner white halo for label */}
      <circle cx={cx} cy={cy} r={r * 0.55} fill="white" opacity={0.92} />
      {/* Labels */}
      <text x={cx} y={cy - 8} textAnchor="middle" className="font-semibold" style={{ fontSize: 14, fill: 'var(--color-fg)' }}>
        {bubble.name}
      </text>
      <text x={cx} y={cy + 10} textAnchor="middle" style={{ fontSize: 22, fontWeight: 700, fill: color }}>
        {bubble.total_points}
      </text>
      <text x={cx} y={cy + 28} textAnchor="middle" style={{ fontSize: 10, fill: 'var(--color-fg-subtle)' }}>
        {bubble.story_count} {bubble.story_count === 1 ? 'story' : 'stories'}
      </text>
    </g>
  )
}
